import * as React from "react";
import { cn } from "@finster/ui";
import { ListTodo, X } from "lucide-react";

/** Keep in sync with `.animate-composer-plan-chip-out` in globals.css. */
export const COMPOSER_PLAN_CHIP_EXIT_MS = 140;

function prefersReducedMotion(): boolean {
  return window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches === true;
}

interface ComposerPlanChipProps {
  open: boolean;
  /**
   * Turns plan mode off. The whole chip is the control — the trailing X is a
   * hover affordance rather than a separate target, so the chip stays reachable
   * by keyboard and on touch, where there is no hover.
   */
  onTurnOff: () => void;
}

/**
 * Active-state indicator for plan mode, shown in the composer's control row
 * only while plan mode is on. Plan mode is armed from the composer's
 * attachment menu; this chip both surfaces and clears it.
 */
export const ComposerPlanChip: React.FC<ComposerPlanChipProps> = ({ open, onTurnOff }) => {
  const [present, setPresent] = React.useState(open);
  const reduceMotion = prefersReducedMotion();

  if (open && !present) {
    setPresent(true);
  } else if (!open && present && reduceMotion) {
    // Exit CSS is `animation: none` under reduced motion, so delaying unmount
    // would leave a fully visible, inert chip until the timeout fires.
    setPresent(false);
  }

  React.useEffect(() => {
    if (open || !present || reduceMotion) return;
    const timeoutId = window.setTimeout(() => setPresent(false), COMPOSER_PLAN_CHIP_EXIT_MS);
    return () => window.clearTimeout(timeoutId);
  }, [open, present, reduceMotion]);

  if (!present || (!open && reduceMotion)) return null;

  const leaving = !open;

  return (
    <button
      type="button"
      aria-pressed
      aria-label="Plan"
      aria-hidden={leaving || undefined}
      tabIndex={leaving ? -1 : undefined}
      onClick={onTurnOff}
      // Tracks the height and radius of the submit button and attachment trigger
      // it sits beside; a shorter chip reads as misaligned in that row.
      className={cn(
        "group inline-flex h-9 shrink-0 cursor-pointer items-center rounded-md px-3",
        "bg-primary-muted text-xs font-medium text-primary",
        "transition-colors duration-150 ease-out",
        "hover:bg-primary-muted/80",
        "active:scale-[0.97] motion-reduce:active:scale-100",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring-focus focus-visible:ring-offset-2",
        leaving
          ? "pointer-events-none animate-composer-plan-chip-out"
          : "animate-composer-plan-chip-in"
      )}
    >
      <span className="inline-flex items-center gap-1.5">
        <ListTodo
          className={cn(
            "h-5 w-5 shrink-0",
            !leaving && "animate-composer-plan-icon-in"
          )}
          aria-hidden
        />
        <span>Plan</span>
      </span>
      <span
        className={cn(
          "grid grid-cols-[0fr] transition-[grid-template-columns] duration-150 ease-out",
          "group-hover:grid-cols-[1fr] group-focus-visible:grid-cols-[1fr]",
          "motion-reduce:transition-none"
        )}
        aria-hidden
      >
        <span className="min-w-0 overflow-hidden">
          <X className="ml-1.5 h-4 w-4" />
        </span>
      </span>
    </button>
  );
};
